/*
* === SecurityHubPage.js (Enterprise-Grade Multi-Tenant Update) ===
*/
import React, { useEffect, useState, useCallback } from 'react';
import { 
    Container, Typography, Card, Box, TextField, Button, Grid, 
    CardHeader, Dialog, DialogTitle, DialogContent, CircularProgress, 
    List, ListItem, ListItemAvatar, Avatar, ListItemText, Divider, 
    FormControl, InputLabel, Select, MenuItem, Chip, DialogActions, 
    IconButton, Tooltip, DialogContentText, Alert, Paper, Switch, FormControlLabel
} from '@mui/material';

import {
    Timeline, TimelineItem, TimelineSeparator, TimelineConnector, 
    TimelineContent, TimelineDot, timelineItemClasses
} from '@mui/lab';

import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Icons
import LockResetIcon from '@mui/icons-material/LockReset';
import GppBadIcon from '@mui/icons-material/GppBad';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import HistoryIcon from '@mui/icons-material/History';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; 
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'; 
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

// --- 🚨 TIMEZONE & DATE SAFE RESOLVER ---
const getSafeDate = (rawDate) => {
    if (!rawDate) return new Date();
    let dateString = typeof rawDate === 'string' ? rawDate : rawDate.toString();
    // Strip the trailing 'Z' to prevent the browser from artificially adding +2 hours (SAST)
    if (dateString.endsWith('Z')) dateString = dateString.slice(0, -1);
    const d = new Date(dateString);
    return isNaN(d) ? new Date() : d;
};

// --- COMPONENTS ---
const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ 
        display: 'flex', alignItems: 'center', padding: 2, height: '100%', cursor: 'pointer',
        transition: 'all 0.2s ease-in-out', 
        '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
        borderLeft: '4px solid', borderImage: `${color} 1`
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 56, height: 56, borderRadius: '12px', marginRight: 2,
        background: color, color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}>
        {React.cloneElement(icon, { fontSize: 'medium' })}
      </Box>
      <Box>
        <Typography variant="h4" fontWeight="800" sx={{ lineHeight: 1.2 }}>{value}</Typography>
        <Typography variant="body2" color="text.secondary" fontWeight="600">{title}</Typography>
      </Box>
    </Card>
);

const MaskedData = ({ revealedValue, onToggleRequest }) => {
    const isVisible = Boolean(revealedValue);
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, border: '1px solid', borderColor: isVisible ? 'error.main' : 'divider', borderRadius: 1, px: 1, bgcolor: isVisible ? 'error.light' : 'background.default' }}>
            <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: isVisible ? 'bold' : 'normal', color: isVisible ? '#fff' : 'text.primary' }}>
                {isVisible ? revealedValue : '••••••••••••'}
            </Typography>
            <Tooltip title={isVisible ? "Hide Data" : "Unmask Data (Logged)"}>
                <IconButton size="small" onClick={onToggleRequest} sx={{ p: 0.5, color: isVisible ? '#fff' : 'inherit' }}>
                    {isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
            </Tooltip>
        </Box>
    );
};

const UnmaskWarningModal = ({ open, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main', fontWeight: 'bold' }}>
            <WarningAmberIcon /> Security Override
        </DialogTitle>
        <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>You are requesting to view highly sensitive <strong>Personally Identifiable Information (PII)</strong> in plain text.</DialogContentText>
            <Alert severity="error" variant="filled" sx={{ fontWeight: 'bold' }}>
                Audit Trail Active: This action is strictly monitored and will be logged against your IP address and User ID.
            </Alert>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
            <Button onClick={onClose} color="inherit">Cancel</Button>
            <Button onClick={onConfirm} variant="contained" color="error">Unmask Data</Button>
        </DialogActions>
    </Dialog>
);

const ActiveSessionsModal = ({ open, onClose, token }) => {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open) {
            setLoading(true);
            api.get('/admin/security-hub/active-sessions', { headers: { Authorization: `Bearer ${token}` } })
               .then(res => setSessions(res.data))
               .catch(err => console.error(err))
               .finally(() => setLoading(false));
        }
    }, [open, token]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
                <AccessTimeIcon color="success" /> Live User Sessions (Last 60m)
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : (
                    <List disablePadding>
                        {sessions.length === 0 ? <Box sx={{ p: 4, textAlign: 'center' }}><Typography color="text.secondary">No active sessions found.</Typography></Box> : 
                         sessions.map((user, index) => {
                            const isGuest = !user.Id;
                            const activeDate = getSafeDate(user.LastActive || user.lastActive);
                            return (
                                <ListItem key={user.Id || `guest-${index}`} divider>
                                    <ListItemAvatar>
                                        <Avatar sx={{ bgcolor: isGuest ? 'grey.500' : (user.Role === 'SuperUser' ? 'secondary.main' : 'primary.main') }}>
                                            {isGuest ? 'G' : (user.FullName ? user.FullName[0] : 'U')}
                                        </Avatar>
                                    </ListItemAvatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="subtitle1" fontWeight="bold">
                                                    {isGuest ? 'Unauthenticated Guest' : user.FullName}
                                                </Typography>
                                                <Chip label={isGuest ? 'Guest' : user.Role} size="small" color={isGuest ? "default" : "primary"} variant="outlined" />
                                            </Box>
                                        }
                                        secondary={
                                            <React.Fragment>
                                                {!isGuest && <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block' }}>{user.CompanyName || 'System Internal'}</Typography>}
                                                <Typography component="span" variant="caption" color="text.secondary">
                                                    Last Active: {activeDate.toLocaleTimeString()} • IP: {user.LastIp || 'Unknown'}
                                                </Typography>
                                            </React.Fragment>
                                        }
                                    />
                                </ListItem>
                            );
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

// 🚨 TARGETED DATA MODAL FIX: Fetches its own data to bypass Page Visit clutter!
const EventListModal = ({ open, onClose, title, filterType, token }) => {
    const [modalLogs, setModalLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (open) {
            setLoading(true);
            // Specifically ask the backend for THIS action type so we don't get empty arrays due to pagination
            api.get('/admin/security-hub/logs', { 
                headers: { Authorization: `Bearer ${token}` },
                params: { action: filterType, limit: 50 } 
            })
            .then(res => setModalLogs(res.data))
            .catch(err => console.error(err))
            .finally(() => setLoading(false));
        }
    }, [open, filterType, token]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 'bold' }}>{title} (Latest 50)</DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box> : 
                 modalLogs.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">No events found for this category.</Typography>
                    </Box>
                ) : (
                    <List disablePadding>
                        {modalLogs.map((log) => {
                            const logDate = getSafeDate(log.ChangedAt || log.changedAt || log.Timestamp || log.timestamp || log.CreatedAt || log.createdAt);
                            let details = log.ChangeDetails;
                            if (typeof details === 'object' && details !== null) {
                                details = Object.entries(details).map(([k, v]) => `${k}: ${v}`).join(' | ');
                            }
                            return (
                                <ListItem key={log.Id} divider alignItems="flex-start">
                                    <ListItemText 
                                        primary={
                                            <Typography fontWeight="bold">{log.Email || log.FullName || 'System / Guest'}</Typography>
                                        }
                                        secondary={
                                            <React.Fragment>
                                                <Typography component="span" variant="caption" color="text.primary" sx={{ display: 'block', mb: 0.5 }}>
                                                    {logDate.toLocaleString()} • IP: {log.IpAddress || 'Unknown'}
                                                </Typography>
                                                <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'error.main', wordBreak: 'break-word' }}>
                                                    {String(details)}
                                                </Typography>
                                            </React.Fragment>
                                        }
                                    />
                                </ListItem>
                            )
                        })}
                    </List>
                )}
            </DialogContent>
            <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
        </Dialog>
    );
};

const auditActionFilters = [
    { value: '', label: 'All Actions' },
    { value: 'APPLICANT_REGISTERED', label: 'Applicant Registered' },
    { value: 'FUNDER_REGISTERED', label: 'Funder Registered' },
    { value: 'PAGE_VISIT', label: 'Page Visit' },
    { value: 'LOGIN_SUCCESS', label: 'Login Success' },
    { value: 'LOGIN_FAILED', label: 'Login Failed' },
    { value: 'PASSWORD_RESET_REQUESTED', label: 'Password Reset Requested' },
    { value: 'USER_PASSWORD_RESET', label: 'Password Reset Completed' },
    { value: 'USER_MODIFIED', label: 'User Modified' },
    { value: 'DOCUMENT_UPLOADED', label: 'Document Uploaded' },
    { value: 'APPLICATION_SUBMITTED', label: 'Application Submitted' },
    { value: 'APPLICATION_STATUS_UPDATE', label: 'Application Status Updated' },
    { value: 'DATA_UNMASKED', label: 'Data Unmasked' },
    { value: 'USER_DELETED', label: 'User Deleted' }
];

const SecurityHubPage = () => {
    const { token } = useAuth();
    const [summary, setSummary] = useState({ failedLogins: 0, permissionChanges: 0, passwordResets: 0, activeSessions: 0 });
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ user: '', action: '', startDate: '', endDate: '' });
    
    // 🚨 NOISE CANCELLER STATE
    const [hidePageVisits, setHidePageVisits] = useState(true);
    
    const [revealedData, setRevealedData] = useState({}); 
    const [unmaskTarget, setUnmaskTarget] = useState(null); 
    const [isDownloading, setIsDownloading] = useState(false); 

    const [isFailedLoginModalOpen, setIsFailedLoginModalOpen] = useState(false);
    const [isPermissionModalOpen, setIsPermissionModalOpen] = useState(false);
    const [isPasswordResetModalOpen, setIsPasswordResetModalOpen] = useState(false); 
    const [isActiveSessionsOpen, setIsActiveSessionsOpen] = useState(false); 
    const [feedbackModal, setFeedbackModal] = useState({ open: false, title: '', message: '', severity: 'info' });

    useEffect(() => {
        const fetchSummary = async () => {
            try {
                const res = await api.get('/admin/security-hub/summary', { headers: { Authorization: `Bearer ${token}` } });
                setSummary(res.data);
            } catch (e) { console.error("Summary fetch failed", e); }
        };
        fetchSummary();
    }, [token]);

    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/security-hub/logs', { 
                headers: { Authorization: `Bearer ${token}` }, params: filters 
            });
            setLogs(res.data);
        } catch (e) { console.error("Log fetch failed", e); } 
        finally { setLoading(false); }
    }, [token, filters]);

    useEffect(() => { fetchLogs(); }, [fetchLogs]);

    const initiateUnmask = (logId, field, currentValue) => {
        const key = `${logId}-${field}`;
        if (revealedData[key]) {
            const newState = { ...revealedData }; delete newState[key]; setRevealedData(newState);
        } else {
            setUnmaskTarget({ logId, field, originalValue: currentValue });
        }
    };

    const handleConfirmUnmask = async () => {
        if (!unmaskTarget) return;
        const { logId, field, originalValue } = unmaskTarget;
        try {
            const res = await api.post('/admin/security-hub/log-unmask', 
                { targetLogId: logId, fieldUnmasked: field || 'SensitiveData' }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const realValue = res.data.unmaskedValue || res.data.value || originalValue || "Value Unavailable"; 
            setRevealedData(prev => ({ ...prev, [`${logId}-${field}`]: realValue }));
            fetchLogs(); 
        } catch (e) { setFeedbackModal({ open: true, title: 'Error', message: 'Failed to unmask data.', severity: 'error' }); } 
        finally { setUnmaskTarget(null); }
    };

    // 🚨 APPLIES NOISE CANCELLER BEFORE RENDER OR EXPORT
    const displayLogs = hidePageVisits ? logs.filter(l => l.ChangeType !== 'PAGE_VISIT') : logs;

    const handleExportCSV = () => { 
        if (!displayLogs.length) { setFeedbackModal({ open: true, title: 'Export Error', message: 'No logs to export.', severity: 'warning' }); return; }
        setIsDownloading(true);
        setTimeout(() => { 
            const csv = Papa.unparse(displayLogs); const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            saveAs(blob, `security_audit_log_${new Date().toISOString().split('T')[0]}.csv`); setIsDownloading(false);
        }, 500);
    };

    const handleExportPDF = () => { 
        if (!displayLogs.length) { setFeedbackModal({ open: true, title: 'Export Error', message: 'No logs to export.', severity: 'warning' }); return; }
        setIsDownloading(true);
        setTimeout(() => {
            const doc = new jsPDF('landscape'); doc.text("Security Audit Log", 14, 15);
            const tableColumn = ["Time", "Action", "User", "IP", "Details"];
            const tableRows = displayLogs.map(log => [
                getSafeDate(log.ChangedAt || log.changedAt || log.Timestamp || log.timestamp || log.CreatedAt || log.createdAt).toLocaleString(), 
                log.ChangeType, 
                log.FullName || log.Email, 
                log.IpAddress || 'Unknown', 
                typeof log.ChangeDetails === 'string' ? log.ChangeDetails : JSON.stringify(log.ChangeDetails)
            ]);
            autoTable(doc, { head: [tableColumn], body: tableRows, startY: 20, styles: { fontSize: 8 }});
            doc.save(`security_audit_log_${new Date().toISOString().split('T')[0]}.pdf`); setIsDownloading(false);
        }, 500);
    };

    const closeFeedbackModal = () => setFeedbackModal({ ...feedbackModal, open: false });

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h4" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center' }}>
                    <SecurityIcon sx={{ mr: 2, fontSize: 40, color: 'primary.main' }} /> Security & Audit Hub
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <FormControlLabel 
                        control={<Switch checked={hidePageVisits} onChange={(e) => setHidePageVisits(e.target.checked)} color="secondary" />} 
                        label={<Typography variant="body2" fontWeight="bold" color={hidePageVisits ? 'secondary.main' : 'text.secondary'}>Hide Traffic Noise</Typography>} 
                        sx={{ mr: 2, bgcolor: 'background.paper', pl: 1, pr: 2, py: 0.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
                    />
                    <Tooltip title="Refresh Data"><IconButton onClick={fetchLogs} disabled={loading} sx={{ bgcolor: 'background.paper', boxShadow: 1 }}><RefreshIcon /></IconButton></Tooltip>
                    <Tooltip title="Export Clean CSV"><IconButton onClick={handleExportCSV} disabled={isDownloading || loading} color="primary" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>{isDownloading ? <CircularProgress size={24} /> : <DownloadIcon />}</IconButton></Tooltip>
                    <Tooltip title="Export Clean PDF"><IconButton onClick={handleExportPDF} disabled={isDownloading || loading} color="error" sx={{ bgcolor: 'background.paper', boxShadow: 1 }}>{isDownloading ? <CircularProgress size={24} /> : <PictureAsPdfIcon />}</IconButton></Tooltip>
                </Box>
            </Box>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}><Box onClick={() => setIsFailedLoginModalOpen(true)}><StatCard title="Failed Logins (24h)" value={summary.failedLogins} icon={<GppBadIcon />} color="linear-gradient(135deg, #FF5252, #D32F2F)" /></Box></Grid>
                <Grid item xs={12} sm={6} md={3}><Box onClick={() => setIsPermissionModalOpen(true)}><StatCard title="Permission Changes" value={summary.permissionChanges} icon={<ManageAccountsIcon />} color="linear-gradient(135deg, #FF9800, #F57C00)" /></Box></Grid>
                <Grid item xs={12} sm={6} md={3}><Box onClick={() => setIsActiveSessionsOpen(true)}><StatCard title="Active User Sessions" value={summary.activeSessions} icon={<PeopleAltIcon />} color="linear-gradient(135deg, #2196F3, #1976D2)" /></Box></Grid>
                <Grid item xs={12} sm={6} md={3}><Box onClick={() => setIsPasswordResetModalOpen(true)}><StatCard title="Password Resets" value={summary.passwordResets} icon={<LockResetIcon />} color="linear-gradient(135deg, #9C27B0, #7B1FA2)" /></Box></Grid>
            </Grid>

            <Paper sx={{ p: 3, mb: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <FilterAltOffIcon color="primary" /> Advanced Audit Filter
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                        <TextField fullWidth size="small" label="Search User / Email" value={filters.user} onChange={e => setFilters({...filters, user: e.target.value})} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Action Type</InputLabel>
                            <Select value={filters.action} label="Action Type" onChange={e => setFilters({...filters, action: e.target.value})}>
                                {auditActionFilters.map(filter => (
                                    <MenuItem key={filter.value} value={filter.value}>{filter.label}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="Start Date" type="date" InputLabelProps={{ shrink: true }} value={filters.startDate} onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}/>
                    </Grid>
                    <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" label="End Date" type="date" InputLabelProps={{ shrink: true }} value={filters.endDate} onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}/>
                    </Grid>
                     <Grid item xs={12} md={2}><Button variant="contained" fullWidth size="large" onClick={fetchLogs}>Apply Filter</Button></Grid>
                </Grid>
            </Paper>

            <Card elevation={3} sx={{ borderRadius: 2 }}>
                <CardHeader 
                    title={<Typography variant="h6" fontWeight="bold">Forensic Audit Trail</Typography>}
                    subheader={`Showing ${displayLogs.length} strict events ${hidePageVisits ? '(Traffic noise suppressed)' : ''}.`}
                    sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }} 
                />
                <Box sx={{ maxHeight: 800, overflow: 'auto', p: 3, bgcolor: 'background.default' }}>
                    {loading ? <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box> : displayLogs.length === 0 ? (
                         <Paper sx={{ p: 5, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }}>
                             <Typography color="text.secondary" variant="h6">No strict security events found.</Typography>
                             {hidePageVisits && <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>Turn off "Hide Traffic Noise" to view raw page visits.</Typography>}
                         </Paper>
                    ) : (
                        <Timeline sx={{ [`& .${timelineItemClasses.root}:before`]: { flex: 0, padding: 0 } }}>
                            {displayLogs.map((log) => {
                                let details = log.ChangeDetails;
                                if (typeof details === 'string') {
                                    try { details = JSON.parse(details); } 
                                    catch (e) { details = { info: details }; }
                                }

                                const isLogin = log.ChangeType === 'LOGIN_SUCCESS';
                                const isFailedLogin = log.ChangeType === 'LOGIN_FAILED';
                                const isCritical = log.ChangeType === 'DATA_UNMASKED' || log.ChangeType === 'USER_DELETED' || log.ChangeType === 'USER_PASSWORD_RESET';
                                
                                const logDate = getSafeDate(log.ChangedAt || log.changedAt || log.Timestamp || log.timestamp || log.CreatedAt || log.createdAt);
                                const timeOnly = logDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
                                const dateOnly = logDate.toLocaleDateString();

                                let dotColor = 'primary';
                                let LogIcon = HistoryIcon;
                                let cardBorderColor = 'transparent';

                                if (isLogin) { dotColor = 'success'; LogIcon = CheckCircleOutlineIcon; } 
                                else if (isFailedLogin) { dotColor = 'error'; LogIcon = GppBadIcon; cardBorderColor = '#ffcdd2'; } 
                                else if (isCritical) { dotColor = 'warning'; LogIcon = ErrorOutlineIcon; cardBorderColor = '#ffe082'; }

                                // 🚨 FORMATTER FIX
                                let humanReadableDetails = '';
                                if (isLogin) humanReadableDetails = `Authentication successful via Secure Gateway.`;
                                else if (isFailedLogin) humanReadableDetails = `Authentication rejected. Incorrect credentials or locked account.`;
                                else if (typeof details === 'object' && details !== null) {
                                    humanReadableDetails = Object.entries(details)
                                        .map(([k, v]) => `• ${k.replace(/([A-Z])/g, ' $1').toUpperCase()}: ${v}`)
                                        .join('\n');
                                } else {
                                    humanReadableDetails = String(details);
                                }

                                return (
                                    <TimelineItem key={`${log.ChangeType}-${log.Id}`}>
                                        <TimelineSeparator>
                                            <TimelineDot color={dotColor} variant={isLogin ? 'filled' : 'outlined'} sx={{ boxShadow: 2 }}><LogIcon fontSize="small" /></TimelineDot>
                                            <TimelineConnector />
                                        </TimelineSeparator>
                                        
                                        <TimelineContent sx={{ py: '12px', px: 2 }}>
                                            <Paper elevation={1} sx={{ p: 2.5, borderLeft: '4px solid', borderColor: cardBorderColor !== 'transparent' ? cardBorderColor : `${dotColor}.main`, mb: 2 }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" color={`${dotColor}.main`} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{log.ChangeType.replace(/_/g, ' ')}</Typography>
                                                    <Box sx={{ textAlign: 'right' }}>
                                                        <Typography variant="body2" fontWeight="bold">{dateOnly}</Typography>
                                                        <Typography variant="caption" color="text.secondary">{timeOnly}</Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                                                    <Chip label={log.FullName?.trim() || log.Email || 'Anonymous Guest'} size="small" variant="outlined" color="primary" sx={{ fontWeight: 'bold' }} />
                                                    {log.Email && (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Typography variant="caption" color="text.secondary" fontWeight="bold">Account ID:</Typography>
                                                            <MaskedData revealedValue={revealedData[`${log.Id}-Email`]} onToggleRequest={() => initiateUnmask(log.Id, 'Email', log.Email)} />
                                                        </Box>
                                                    )}
                                                    <Chip label={`IP: ${log.IpAddress || 'Hidden'}`} size="small" sx={{ ml: 'auto', fontFamily: 'monospace' }} />
                                                </Box>

                                                <Box sx={{ mt: 1, bgcolor: 'action.hover', p: 1.5, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                                    <Typography component="pre" variant="body2" sx={{ margin: 0, fontFamily: 'monospace', fontSize: '0.85rem', color: isCritical ? 'error.main' : 'text.primary', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.6 }}>
                                                        {humanReadableDetails}
                                                    </Typography>
                                                </Box>
                                            </Paper>
                                        </TimelineContent>
                                    </TimelineItem>
                                );
                            })}
                        </Timeline>
                    )}
                </Box>
            </Card>

            <UnmaskWarningModal open={Boolean(unmaskTarget)} onClose={() => setUnmaskTarget(null)} onConfirm={handleConfirmUnmask} />

            <Dialog open={feedbackModal.open} onClose={closeFeedbackModal}>
                <DialogTitle sx={{ color: feedbackModal.severity === 'error' ? 'error.main' : 'info.main', fontWeight: 'bold' }}>{feedbackModal.title}</DialogTitle>
                <DialogContent><DialogContentText>{feedbackModal.message}</DialogContentText></DialogContent>
                <DialogActions><Button onClick={closeFeedbackModal} variant="contained" color="primary">Acknowledge</Button></DialogActions>
            </Dialog>

            {/* 🚨 UPDATED MODALS: Now pass the Token so they fetch their own data! */}
            <EventListModal open={isFailedLoginModalOpen} onClose={() => setIsFailedLoginModalOpen(false)} title="Failed Logins" filterType="LOGIN_FAILED" token={token} />
            <EventListModal open={isPermissionModalOpen} onClose={() => setIsPermissionModalOpen(false)} title="Recent Permission Changes" filterType="USER_MODIFIED" token={token} />
            <EventListModal open={isPasswordResetModalOpen} onClose={() => setIsPasswordResetModalOpen(false)} title="Recent Password Resets" filterType="USER_PASSWORD_RESET" token={token} />
            <ActiveSessionsModal open={isActiveSessionsOpen} onClose={() => setIsActiveSessionsOpen(false)} token={token} />
        </Container>
    );
};

export default SecurityHubPage;